import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminRoofType,
  InsertAdminRoofTypeBody,
  UpdateAdminRoofTypeNewImageBody,
  UpdateAdminRoofTypeRemoveImageBody,
} from "./types/roofTypes";

export const useRoofTypesApi = () => {
  const api = useApi();

  const getAdminRoofType = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/roof_types`;
      return useSWRMutation<AdminRoofType, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<AdminRoofType>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminRoofTypes = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/patrol/roof_types", query);
      return useSWR<Paginated<AdminRoofType>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminRoofType>>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminCategoryRoofTypes = useCallback(
    (categoryId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/patrol/roof_categories/${categoryId}/roof_types`, query);
      return useSWR<Paginated<AdminRoofType>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminRoofType>>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminSystemRoofTypes = useCallback(
    (systemId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/patrol/systems/${systemId}/roof_types`, query);
      return useSWR<Paginated<AdminRoofType>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminRoofType>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminRoofType = useCallback(
    async (body: InsertAdminRoofTypeBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/roof_types`;
      const id = await api.post<{ id: string }, InsertAdminRoofTypeBody>(url, body);
      mutateCache([`/admin/settings/patrol/roof_categories/${body.categoryId}/roof_types`, url]);
      return id;
    },
    [api],
  );

  const updateAdminRoofType = useCallback(
    async (
      roofTypeId: string,
      body: UpdateAdminRoofTypeRemoveImageBody | UpdateAdminRoofTypeNewImageBody,
    ): Promise<void> => {
      const url = `/admin/settings/patrol/roof_types/${roofTypeId}`;
      (await api.put<UpdateAdminRoofTypeRemoveImageBody | UpdateAdminRoofTypeNewImageBody>(url, body),
        mutateCache(["/admin/settings/patrol/roof_types"]));
    },
    [api],
  );

  const deleteAdminRoofType = useCallback(
    async (roofTypeId: string, categoryId?: string, systemId?: string): Promise<void> => {
      const url = `/admin/settings/patrol/roof_types/${roofTypeId}`;
      await api.del(url);
      mutateCache([
        `/admin/settings/patrol/roof_types`,
        `/admin/settings/patrol/roof_categories/${categoryId}/roof_types`,
        `/admin/settings/patrol/roof_categories/${systemId}/roof_types`,
      ]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (roofTypeId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/patrol/roof_types/${roofTypeId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  const updateAdminRoofTypeVisibility = useCallback(
    async (
      roofTypeId: string,
      visibility: UpdateVisibilityRequestBody,
      options?: { categoryId?: string; systemId?: string },
    ): Promise<void> => {
      const url = `/admin/settings/patrol/roof_types/${roofTypeId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([
        "/admin/settings/patrol/roof_types",
        `/admin/settings/patrol/roof_categories/${options?.categoryId}/roof_types`,
        `/admin/settings/patrol/roof_categories/${options?.systemId}/roof_types`,
      ]);
    },
    [api],
  );

  const updateAdminRoofTypeOrder = useCallback(
    async (
      roofTypeId: string,
      order: UpdateOrderRequestBody,
      options?: { categoryId?: string; systemId?: string },
    ): Promise<void> => {
      const url = `/admin/settings/patrol/roof_types/${roofTypeId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([
        "/admin/settings/patrol/roof_types",
        `/admin/settings/patrol/roof_categories/${options?.categoryId}/roof_types`,
        `/admin/settings/patrol/roof_categories/${options?.systemId}/roof_types`,
      ]);
    },
    [api],
  );

  return {
    getAdminRoofType,
    useAdminRoofTypes,
    insertAdminRoofType,
    updateAdminRoofType,
    updateAdminRoofTypeVisibility,
    updateAdminRoofTypeOrder,
    useAdminCategoryRoofTypes,
    useAdminSystemRoofTypes,
    deleteAdminRoofType,
    generateUploadImageUrl,
  };
};
